import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../context/OrganizationContext';
import { apiService } from '../services/api';

export const OrganizationStatusChecker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { currentOrganization, organizations, refreshOrganizations } = useOrganization();
  const abortControllerRef = useRef<AbortController | null>(null);
  const checkInProgressRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    // Check organization status periodically
    const checkOrganizationStatus = async () => {
      if (!currentOrganization || checkInProgressRef.current) return;

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      checkInProgressRef.current = true;

      try {
        // Try to fetch current organization details with abort signal
        const orgDetails = await apiService.api.get(
          `/api/organizations/${currentOrganization.id}`,
          { signal: abortControllerRef.current.signal }
        );
        
        // If we get here, organization is still active
      } catch (error: any) {
        // Ignore if request was aborted
        if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
          return;
        }

        if (mounted && error.response?.status === 404) {
          // Organization not found or deactivated
          console.log('Current organization has been deactivated');
          
          // Show alert
          alert(`The organization "${currentOrganization.name}" has been deactivated. You will be redirected to select another organization.`);
          
          // Refresh organization list
          try {
            await refreshOrganizations();
          } catch (refreshError) {
            console.error('Failed to refresh organizations:', refreshError);
          }
          
          // Clear current org from localStorage
          localStorage.removeItem('current_org_id');
          
          // Navigate to account page
          if (mounted) {
            navigate('/account');
          }
        }
      } finally {
        checkInProgressRef.current = false;
      }
    };

    // Initial check after a small delay to avoid immediate requests
    const initialTimeout = setTimeout(() => {
      if (mounted) {
        checkOrganizationStatus();
      }
    }, 2000);

    // Set up periodic check
    const interval = setInterval(() => {
      if (mounted) {
        checkOrganizationStatus();
      }
    }, 60000); // Check every 60 seconds instead of 30 to reduce load

    return () => {
      mounted = false;
      clearTimeout(initialTimeout);
      clearInterval(interval);
      
      // Abort any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [currentOrganization?.id, navigate, refreshOrganizations]);

  return <>{children}</>;
};